import React from 'react';
import { FileText, Calendar, Clock, MapPin, DollarSign, User } from 'lucide-react';
import { formatTime12Hour } from '../utils/timeFormat';

const ReceiptTemplate = ({ receipt }) => {
  return (
    <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
      {/* Header - Aqua Strip */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="/images/taxi98-logo.png" 
              alt="Taxi-98"
              className="h-16 w-auto bg-white rounded-lg p-2"
            />
            <div>
              <h1 className="text-2xl font-bold text-white">Taxi-98</h1>
              <p className="text-cyan-100 text-sm">Your trusted ride in Nassau</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white font-bold">RECEIPT</p>
            <p className="text-cyan-100 text-sm">{receipt.reference}</p>
          </div>
        </div>
      </div>

      {/* Body - Trip Details */}
      <div className="p-8">
        {/* Customer Info */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Customer Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Customer Name</p>
              <p className="font-semibold text-gray-900">{receipt.customerName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-semibold text-gray-900">{receipt.customerEmail}</p>
            </div>
          </div>
        </div>

        {/* Trip Details */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Trip Details</h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-cyan-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-semibold text-gray-900">{receipt.tripDate}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-cyan-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Drop-off Time</p>
                <p className="font-semibold text-gray-900">{formatTime12Hour(receipt.dropoffTime)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">Pickup Location</p>
                <p className="font-semibold text-gray-900">{receipt.pickupLocation}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-coral-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">Drop-off Location</p>
                <p className="font-semibold text-gray-900">{receipt.dropoffLocation}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <User className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Driver</p>
                <p className="font-semibold text-gray-900">{receipt.driverName}</p>
                {receipt.driverId && (
                  <p className="text-xs text-gray-500">ID: {receipt.driverId}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fare Information */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Payment Information</h2>
          
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-6 h-6 text-cyan-600" />
                <span className="text-gray-700 font-medium">Fare Collected</span>
              </div>
              <span className="text-3xl font-bold text-cyan-700">
                ${Number(receipt.fareCollected).toFixed(2)}
              </span>
            </div>
            <p className="text-sm text-gray-600">BSD (Bahamian Dollar)</p>
          </div>

          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 italic">
              <strong>Note:</strong> {receipt.notes}
            </p>
          </div>
        </div>

        {/* Footer Note */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            <strong>Disclaimer:</strong> This receipt is for record purposes only. No online payment was processed. 
            Payment was collected directly by the driver.
          </p>
        </div>
      </div>

      {/* Footer - Contact Info */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center md:text-left">
          <div>
            <p className="text-cyan-100 text-xs">Phone</p>
            <p className="font-semibold text-sm">+1 (242) 810-7335</p>
          </div>
          <div>
            <p className="text-cyan-100 text-xs">Email</p>
            <p className="font-semibold text-sm">bookings@taxi-98.com</p>
          </div>
          <div>
            <p className="text-cyan-100 text-xs">Website</p>
            <p className="font-semibold text-sm">taxi-98.com</p>
          </div>
        </div>
        <p className="text-cyan-100 text-xs text-center mt-4">
          Nassau, New Providence, Bahamas • Est. 2026
        </p>
      </div>
    </div>
  );
};

export default ReceiptTemplate;
